import type { CSSProperties, KeyboardEvent } from 'react';
import { BOARD_THEMES, useBoardTheme, writeBoardTheme, type BoardTheme } from '../board/theme';
import type { UciMove } from '../api/types';
import { BoardView } from './BoardView';

/** 1.e4 c6: a prepared move (green), a played mistake (red) and an alternative (blue). */
const PREVIEW = {
  fen: 'rnbqkbnr/pp1ppppp/2p5/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 0 2',
  lastMove: 'c7c6' as UciMove,
  arrows: [
    { uci: 'd2d4' as UciMove, brush: 'green' as const },
    { uci: 'f1c4' as UciMove, brush: 'red' as const },
    { uci: 'b1c3' as UciMove, brush: 'blue' as const },
  ],
};

function swatchStyle(theme: BoardTheme): CSSProperties {
  return {
    backgroundImage: `conic-gradient(${theme.dark} 25%, ${theme.light} 0 50%, ${theme.dark} 0 75%, ${theme.light} 0)`,
  };
}

/**
 * Choose the board skin for every board on the site. The preview is a real board, so
 * it shows exactly what the builder, practice and game review will look like.
 */
export function BoardThemePicker(): JSX.Element {
  const active = useBoardTheme();

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const index = BOARD_THEMES.findIndex((theme) => theme.id === active.id);
    const next = BOARD_THEMES[(index + step + BOARD_THEMES.length) % BOARD_THEMES.length];
    writeBoardTheme(next.id);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-theme-id="${next.id}"]`)?.focus();
  }

  return (
    <div className="board-theme-picker">
      <div role="radiogroup" aria-label="Board skin" className="board-theme-options" onKeyDown={onKeyDown}>
        {BOARD_THEMES.map((theme) => {
          const checked = theme.id === active.id;
          return (
            <button
              key={theme.id}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              data-theme-id={theme.id}
              className={`board-theme-option${checked ? ' current' : ''}`}
              onClick={() => writeBoardTheme(theme.id)}
            >
              <span className="board-theme-swatch" style={swatchStyle(theme)} aria-hidden="true" />
              <span>{theme.name}</span>
            </button>
          );
        })}
      </div>
      <div className="board-theme-preview">
        <BoardView
          input={{ fen: PREVIEW.fen, turnColor: 'w', orientation: 'w', dests: [], lastMove: PREVIEW.lastMove, arrows: PREVIEW.arrows }}
          onMove={() => false}
          label={`Board preview in the ${active.name} skin`}
        />
        <ul className="arrow-legend" aria-label="Arrow colours">
          <li className="arrow-swatch-green">Your prepared move</li>
          <li className="arrow-swatch-red">A move you played</li>
          <li className="arrow-swatch-blue">An alternative</li>
        </ul>
      </div>
    </div>
  );
}
